import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, CardContent, Typography, Box, LinearProgress, Snackbar, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ItemProject from './ItemProject';
import SendEmailProject from './SendEmailProject';
import AlertMessage from '../../AlertMessage';
import getMyJackSyncTaskStatus from '../../../Apollo/queries/getMyJackSyncTaskStatus';
import getUserProjectById from '../../../Apollo/queries/getUserProjectById';
import sendAgentToCentralNodeQuery from '../../../Apollo/mutations/sendAgentToCentralNodeQuery';
import { ProjectType, ProjectTypeEnums } from '../../../types/Project.types';
import { ContractTypeEnums } from '../../../types/ContractType.types';
import syncAgentToMyJackQuery from '../../../Apollo/mutations/syncAgentToMyJack';

interface Props {
  id: string;
  agentId: string | number;
  contractId: number | null;
}

function AgentProjects({ id, agentId, contractId }: Props) {
  const {
    data: getUserProjectData,
    loading: projectsLoading,
    error: projectsError,
    refetch,
  } = useQuery(getUserProjectById, {
    variables: { id },
    fetchPolicy: 'network-only', // Daten immer neu laden
    skip: !id, // Skip, wenn keine ID vorhanden ist
  });

  const [sendAgentToCentralNode, { data, error, reset: resetCentralNode }] = useMutation<{
    sendAgentsToCentralNode: boolean;
  }>(sendAgentToCentralNodeQuery);

  const [syncAgentToMyJack, { data: myJackData, error: myJackDataError, reset: resetMyJack }] = useMutation<{
    syncAgentToMyJack: boolean;
  }>(syncAgentToMyJackQuery);

  // MyJack UI state
  const [enablePolling, setEnablePolling] = useState(false);
  const [localSyncRunning, setLocalSyncRunning] = useState(false);
  const [localProgress, setLocalProgress] = useState(0);

  // MyJack task status query
  const {
    data: taskStatusData,
    loading: taskStatusLoading,
    refetch: refetchTaskStatus,
    startPolling,
    stopPolling,
  } = useQuery(getMyJackSyncTaskStatus, {
    fetchPolicy: 'network-only',
    // Kein pollInterval hier - wird dynamisch aktiviert
  });

  // MyJack polling logic
  useEffect(() => {
    if (!taskStatusLoading && taskStatusData) {
      const status = taskStatusData?.getMyJackSyncTaskStatus?.status;
      const progress = taskStatusData?.getMyJackSyncTaskStatus?.progress || 0;

      // Wenn Task läuft oder nicht komplett abgeschlossen ist, Polling aktivieren
      if (status === 'running' || (status === 'done' && progress < 100)) {
        if (!enablePolling) {
          setEnablePolling(true);
          startPolling(3000); // 3 Sekunden Intervall
        }
      } else if (enablePolling) {
        // Wenn Task abgeschlossen, Polling stoppen
        setEnablePolling(false);
        stopPolling();
      }
    }
  }, [taskStatusData, taskStatusLoading, enablePolling, startPolling, stopPolling]);

  // MyJack status extraction
  const taskStatus = taskStatusData?.getMyJackSyncTaskStatus?.status || 'done';
  const taskProgress = taskStatusData?.getMyJackSyncTaskStatus?.progress || 100;

  // MyJack sync status checks
  const isSyncRunning = taskStatus === 'running';
  const isSyncEnabled = (taskStatus === 'done' || taskStatus === 'error') && !localSyncRunning;

  // MyJack UI display status
  const displaySyncRunning = localSyncRunning || isSyncRunning;
  const displayProgress = isSyncRunning ? taskProgress : localProgress;

  // MyJack success message state
  const [showCompletionSuccess, setShowCompletionSuccess] = useState(false);
  const prevStatusRef = useRef(taskStatus);
  const snackbarTimeoutRef = useRef<number | null>(null);

  // MyJack success message function
  const showSuccessMessage = () => {
    // Alten Timer löschen, falls vorhanden
    if (snackbarTimeoutRef.current) {
      clearTimeout(snackbarTimeoutRef.current);
    }

    // Erfolgsmeldung anzeigen
    setShowCompletionSuccess(true);

    // Neuen Timer setzen
    snackbarTimeoutRef.current = window.setTimeout(() => {
      setShowCompletionSuccess(false);
      snackbarTimeoutRef.current = null;
    }, 5000);
  };

  // MyJack status change detection
  useEffect(() => {
    const prevStatus = prevStatusRef.current;

    // Wenn Status auf "done" wechselt und Fortschritt 100% ist
    // Wir prüfen nicht nur den Wechsel von "running", um auch schnelle Synchronisationen zu erfassen
    if (taskStatus === 'done' && taskProgress === 100 && prevStatus !== 'done') {
      showSuccessMessage();
      setLocalSyncRunning(false); // Lokalen Zustand zurücksetzen
      refetch(); // Reload project data to show specific errors from DB
    }

    // Status für den nächsten Vergleich speichern
    prevStatusRef.current = taskStatus;
  }, [taskStatus, taskProgress, refetch]);

  // MyJack API response handling
  useEffect(() => {
    // Wenn wir eine erfolgreiche Antwort von der Mutation erhalten
    if (myJackData?.syncAgentToMyJack === true && !myJackDataError) {
      showSuccessMessage();
    }
  }, [myJackData, myJackDataError]);

  // MyJack automatic reset logic
  useEffect(() => {
    if (localSyncRunning) {
      // Schrittweise Fortschritt erhöhen, um visuelles Feedback zu geben
      const progressTimer = setInterval(() => {
        setLocalProgress((prev) => Math.min(prev + 5, 90)); // Maximal 90%, der Rest kommt vom echten Fortschritt
      }, 300);

      // Mindestdauer der Anzeige (2 Sekunden)
      const resetTimer = setTimeout(() => {
        // Zurücksetzen, wenn der Task nicht mehr läuft oder abgeschlossen ist
        if (taskStatus !== 'running') {
          setLocalSyncRunning(false);
          clearInterval(progressTimer);
        }
      }, 2000);

      return () => {
        clearTimeout(resetTimer);
        clearInterval(progressTimer);
      };
    }
    return undefined;
  }, [localSyncRunning, taskStatus]);

  // MyJack error logging
  if (myJackData && (myJackData as any).errors) {
    // console.error('GraphQL Fehler:', (myJackData as any).errors);
  }

  return (
    <>
      {projectsLoading && (
        <Card sx={{ marginBottom: '2%' }}>
          <CardContent>
            <Typography>Projekte werden geladen...</Typography>
          </CardContent>
        </Card>
      )}

      {projectsError && (
        <Card sx={{ marginBottom: '2%' }}>
          <CardContent>
            <Typography color="error">Fehler beim Laden der Projekte: {projectsError.message}</Typography>
          </CardContent>
        </Card>
      )}

      {!projectsLoading &&
        !projectsError &&
        (!getUserProjectData?.getUsersProjectByID || getUserProjectData.getUsersProjectByID.length === 0) && (
          <Card sx={{ marginBottom: '2%' }}>
            <CardContent>
              <Typography>Keine Projekte gefunden</Typography>
            </CardContent>
          </Card>
        )}

      {!projectsLoading &&
        !projectsError &&
        getUserProjectData?.getUsersProjectByID?.map((projectItem: ProjectType) => (
          <Card sx={{ marginBottom: '2%' }}>
            <CardContent>
              <ItemProject
                title={projectItem.project.title}
                date={projectItem.updatedAt}
                status={projectItem.error}
                refreshDate={() => {
                  if (projectItem.project.id === ProjectTypeEnums.MyJack) {
                    // Nur synchronisieren, wenn keine Synchronisation läuft
                    if (isSyncEnabled) {
                      // Sofortige UI-Reaktion
                      setLocalSyncRunning(true);
                      setLocalProgress(5); // Initiales Feedback

                      // API-Anfragen starten
                      syncAgentToMyJack({ variables: { agentID: agentId } }).then((result) => {
                        if (result.data) {
                          refetch();
                          refetchTaskStatus(); // Taskstatus aktualisieren
                        }
                      });

                      // Also update CentralNode just in case
                      sendAgentToCentralNode({ variables: { agentID: agentId } }).then((result) => {
                        if (result.data) refetch();
                      });
                    }
                  }

                  // Only handle CentralNode synchronization for now
                  if (projectItem.project.id === ProjectTypeEnums.CentralNode) {
                    sendAgentToCentralNode({ variables: { agentID: agentId } }).then((result) => {
                      if (result.data) refetch();
                    });
                  }
                }}
                // Enable MyJack project button
                isDisabled={projectItem.project.id === ProjectTypeEnums.MyJack && !isSyncEnabled}
                disabledTooltip={
                  projectItem.project.id === ProjectTypeEnums.MyJack && !isSyncEnabled
                    ? 'Synchronisation läuft bereits.'
                    : undefined
                }
              />
              {contractId === ContractTypeEnums.ProHolidays && (
                <Typography style={{ color: 'gray', fontWeight: 'normal', marginBottom: '5%' }}>
                  Keine Passwortgenerierung für Vertragsart &quot;ProHolidays&quot;
                </Typography>
              )}
              {projectItem.project.id === ProjectTypeEnums.CentralNode && (
                <SendEmailProject
                  id={id}
                  isDisabled={!projectItem.updatedAt || contractId === ContractTypeEnums.ProHolidays}
                />
              )}
            </CardContent>
          </Card>
        ))}
      <AlertMessage
        typeMessage={data?.sendAgentsToCentralNode && !error ? 'success' : 'error'}
        alertMessage={
          data?.sendAgentsToCentralNode && !error ? 'Synchronisation erfolgreich' : 'Synchronisation fehlgeschlagen'
        }
        isAlertMessage={!!data || !!error}
        handleChangeIsAlertMessage={resetCentralNode}
      />

      <AlertMessage
        typeMessage="error"
        alertMessage="Synchronisation fehlgeschlagen"
        isAlertMessage={!!myJackDataError}
        handleChangeIsAlertMessage={resetMyJack}
      />

      {displaySyncRunning && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Typography variant="body2" gutterBottom>
            Synchronisierung läuft... ({displayProgress}%)
          </Typography>
          <LinearProgress variant="determinate" value={displayProgress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Bitte warten Sie, bis die Synchronisierung abgeschlossen ist.
          </Typography>
        </Box>
      )}

      <Snackbar
        open={showCompletionSuccess}
        autoHideDuration={5000}
        onClose={() => {
          setShowCompletionSuccess(false);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }} icon={<CheckCircleIcon fontSize="inherit" />}>
          Synchronisation erfolgreich abgeschlossen!
        </Alert>
      </Snackbar>
    </>
  );
}

export default AgentProjects;
